import React, { useEffect, useState } from "react";
import { Card, Table, DatePicker, Modal, Calendar, Space } from "antd";
import {
  TeamOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { formatTimeInTz } from "../../helper/timezone";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import StatCard from "../../components/ui/StatCard";
import StatusTag from "../../components/ui/StatusTag";
import EmptyState from "../../components/ui/EmptyState";
import ViewToggle from "../../components/ui/ViewToggle";

const Dashboard = () => {
  const { request } = useRequest();
  const [summary, setSummary] = useState({});
  const [sessions, setSessions] = useState([]);
  const [date, setDate] = useState(moment());
  const [drilldown, setDrilldown] = useState(null); // { title, rows }
  const [view, setView] = useState("list");
  const [calendarData, setCalendarData] = useState({ totalEmployees: 0, days: {} });

  const fetchDashboard = (d) => {
    request({
      url: `${apiPath.attendanceDashboard}?date=${d.format("YYYY-MM-DD")}`,
      method: "GET",
      onSuccess: (res) => {
        setSummary(res?.data?.summary || {});
        setSessions(res?.data?.sessions || []);
      },
    });
  };

  useEffect(() => {
    fetchDashboard(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCalendarSummary = (momentDate) => {
    request({
      url: `${apiPath.attendanceCalendarSummary}?month=${momentDate.month() + 1}&year=${momentDate.year()}`,
      method: "GET",
      onSuccess: (res) => {
        setCalendarData({ totalEmployees: res?.data?.totalEmployees || 0, days: res?.data?.days || {} });
      },
    });
  };

  useEffect(() => {
    if (view === "calendar") fetchCalendarSummary(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const columns = [
    { title: "Employee", dataIndex: ["employee", "name"], key: "name" },
    { title: "Employee ID", dataIndex: ["employee", "employeeId"], key: "employeeId" },
    { title: "Office", dataIndex: ["office", "name"], key: "office" },
    {
      title: "Shift Start",
      dataIndex: "loginAt",
      key: "loginAt",
      render: (v, record) => formatTimeInTz(v, record.office?.timezone), // in the employee's office timezone
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <StatusTag status={s} />,
    },
  ];

  // Each stat tile's own definition of "who counts" — kept in one place so
  // the number on the card and the list you get on clicking it can never
  // disagree with each other.
  const statDefs = [
    {
      key: "total",
      label: "Total Sessions",
      icon: <TeamOutlined />,
      color: "#4F46E5",
      value: summary.total || 0,
      filter: () => true,
    },
    {
      key: "present",
      label: "Present",
      icon: <CheckCircleOutlined />,
      color: "#14B8A6",
      value: summary.present || 0,
      filter: (s) => s.status === "present",
    },
    {
      key: "wfh",
      label: "WFH",
      icon: <HomeOutlined />,
      color: "#3B82F6",
      value: summary.wfh || 0,
      filter: (s) => s.status === "wfh",
    },
    {
      key: "outsideGeofence",
      label: "Outside Geofence",
      icon: <EnvironmentOutlined />,
      color: "#EF4444",
      // Live signal: currently outside and still within the auto-logout
      // grace window — not the "outside_geofence" status value, which only
      // ever applies for the few checks before a session either recovers
      // or gets auto-closed.
      value: summary.outsideGeofence || 0,
      filter: (s) => s.isActive && s.isCurrentlyOutside,
    },
    {
      key: "autoLoggedOut",
      label: "Auto Logged Out",
      icon: <LogoutOutlined />,
      color: "#F59E0B",
      value: summary.autoLoggedOut || 0,
      filter: (s) => s.status === "auto_logged_out",
    },
  ];

  const openDrilldown = (def) => {
    setDrilldown({ title: def.label, rows: sessions.filter(def.filter) });
  };

  const dateCellRender = (value) => {
    const dateStr = value.format("YYYY-MM-DD");
    const day = calendarData.days[dateStr];
    if (!day) return null;
    const rows = [
      { label: "Present", value: day.present, color: "#14B8A6" },
      { label: "WFH", value: day.wfh, color: "#3B82F6" },
      { label: "Absent", value: day.absent, color: "#9CA3AF" },
      { label: "On Leave", value: day.onLeave, color: "#8B5CF6" },
      { label: "Holiday", value: day.holiday, color: "#6366F1" },
    ].filter((r) => r.value > 0);
    return (
      <div style={{ opacity: day.isWeekend ? 0.55 : 1 }}>
        {rows.map((r) => (
          <div
            key={r.label}
            style={{
              fontSize: 11,
              lineHeight: "16px",
              color: r.color,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {r.label}: {r.value}
          </div>
        ))}
      </div>
    );
  };

  const onCalendarPanelChange = (value) => {
    fetchCalendarSummary(value);
  };

  const onCalendarSelect = (value) => {
    setDate(value);
    fetchDashboard(value);
    setView("list");
  };

  return (
    <PageTransition>
      <PageHeader
        title="Attendance Snapshot"
        subtitle="Live overview of today's attendance across your organization"
        actions={
          <Space>
            <ViewToggle value={view} onChange={setView} />
            {view === "list" && (
              <DatePicker
                value={date}
                onChange={(d) => {
                  const nd = d || moment();
                  setDate(nd);
                  fetchDashboard(nd);
                }}
              />
            )}
          </Space>
        }
      />
      <div className="i-ems-stat-row">
        {statDefs.map((def) => (
          <StatCard
            key={def.key}
            icon={def.icon}
            label={def.label}
            value={def.value}
            color={def.color}
            onClick={() => openDrilldown(def)}
          />
        ))}
      </div>
      {view === "list" ? (
        <Card title="Today's Sessions">
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={sessions}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <EmptyState message="No sessions recorded for this date" /> }}
          />
        </Card>
      ) : (
        <Card title="Attendance Calendar">
          <Calendar
            dateCellRender={dateCellRender}
            onPanelChange={onCalendarPanelChange}
            onSelect={onCalendarSelect}
          />
        </Card>
      )}

      <Modal
        title={drilldown ? `${drilldown.title} — ${drilldown.rows.length}` : ""}
        open={!!drilldown}
        onCancel={() => setDrilldown(null)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={drilldown?.rows || []}
          pagination={{ pageSize: 8 }}
          size="small"
          locale={{ emptyText: <EmptyState message="No employees match this right now" /> }}
        />
      </Modal>
    </PageTransition>
  );
};

export default Dashboard;
